import {
  ChangeEvent,
  ClipboardEvent,
  FocusEvent,
  forwardRef,
  InputHTMLAttributes,
  useMemo,
} from "react";
import { withValidate } from "./withValidate";
import { twMerge } from "tailwind-merge";
import { useCallback } from "react";
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  regexp?: "date" | "business-number";
}
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      regexp,
      className,
      type,
      onChange,
      inputMode: initialInputMode,
      onBlur,
      ...props
    },
    ref
  ) => {
    const inputMode = useMemo(() => {
      if (initialInputMode) return initialInputMode;
      switch (type) {
        case "tel":
        case "number":
        case "date":
          return "numeric";

        default:
          return "text";
      }
    }, [type, initialInputMode]);
    const handlePaste = useCallback(
      (event: ClipboardEvent<HTMLInputElement>) => {
        const { clipboardData, currentTarget } = event;
        // let formattedValue = target.value;
        switch (type) {
          case "tel": {
            const data = clipboardData.getData("Text");
            if (data.length === 10 && data.startsWith("10")) {
              currentTarget.value = `0${data}`;
            }
            break;
          }
        }
      },
      [type, onChange, regexp]
    );
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { target } = event;
        let formattedValue = target.value;
        switch (type) {
          case "decimal":
            formattedValue = formattedValue.replace(/[^\d.]/g, "");
            break;
          case "tel":
            formattedValue = formattedValue.replace(/\D/g, ""); // 숫자가 아닌 모든 문자를 제거합니다.

            if (formattedValue.length > 3 && formattedValue.length <= 7) {
              formattedValue = formattedValue.replace(/(\d{3})(\d+)/, "$1-$2");
            } else if (formattedValue.length > 7) {
              formattedValue = formattedValue.replace(
                /(\d{3})(\d{3,4})(\d{1,4})/,
                "$1-$2-$3"
              );
            }
            formattedValue = formattedValue.substring(0, 13);
            break;
          default:
            switch (regexp) {
              case "date":
                formattedValue = formattedValue.replace(/\D/g, ""); // 숫자가 아닌 모든 문자를 제거합니다.

                if (formattedValue.length > 4 && formattedValue.length <= 6) {
                  formattedValue = formattedValue.replace(
                    /(\d{4})(\d+)/,
                    "$1-$2"
                  );
                } else if (formattedValue.length > 6) {
                  formattedValue = formattedValue.replace(
                    /(\d{4})(\d{2})(\d{1,2})/,
                    "$1-$2-$3"
                  );
                }
                formattedValue = formattedValue.substring(0, 10);
                break;
              case "business-number":
                formattedValue = formattedValue.replace(/\D/g, ""); // 숫자가 아닌 모든 문자를 제거합니다.

                if (formattedValue.length > 3 && formattedValue.length <= 5) {
                  formattedValue = formattedValue.replace(
                    /(\d{3})(\d+)/,
                    "$1-$2"
                  );
                } else if (formattedValue.length > 5) {
                  formattedValue = formattedValue.replace(
                    /(\d{3})(\d{2})(\d{1,5})/,
                    "$1-$2-$3"
                  );
                }
                formattedValue = formattedValue.substring(0, 12); // 사업자번호 최대 길이 제한
                break;
            }
        }
        target.value = formattedValue;

        onChange?.(event);
      },
      [type, onChange, regexp]
    );
    const handleBlur = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        onBlur?.(e);
      },
      [onBlur]
    );
    return (
      <label className={twMerge("flex flex-col", className)}>
        <input
          {...props}
          inputMode={inputMode}
          ref={ref}
          type={type}
          onBlur={handleBlur}
          onChange={handleChange}
          onPaste={handlePaste}
          className="outline-none transition-all h-10 p-2.5 border border-inherit rounded disabled:bg-[#E9E9E9] disabled:text-[#666666] focus:outline-yellow"
        />
      </label>
    );
  }
);

export const ValidateInput = withValidate(Input);
